import { useEffect, useState } from "react";
import { LogIn, Coffee, Flag, CircleDot, List, CalendarDays, MapPin, Pencil } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar, StatusBadge } from "@/components/common";
import CheckInCard from "@/components/CheckInCard";
import { EmptyState } from "@/components/ui-bits";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { timeStr, shortDate } from "@/lib/format";
import AttendanceMonthDialog from "@/components/AttendanceMonthDialog";
import ManualChangeNote from "@/components/ManualChangeNote";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export default function Attendance() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin" || user.role === "team_leader" || user.role === "admin_staff";
  return isAdmin ? <AdminAttendance user={user} /> : <StaffAttendance />;
}

function Timeline({ today, shift }) {
  if (!today?.check_in)
    return <EmptyState icon={LogIn} title="Your workday hasn't started yet." subtitle="Check in to begin tracking your day." />;
  const fmtShift = (hm) => { if (!hm) return "—"; const [h, m] = hm.split(":").map(Number); const ap = h >= 12 ? "PM" : "AM"; return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${ap}`; };
  const events = [
    { time: timeStr(today.check_in), label: today.check_in_office ? `Check In (${today.check_in_office})` : "Check In", icon: CircleDot, color: "text-emerald-500", bg: "bg-emerald-50" },
  ];
  if (today.check_out) {
    const co = { time: timeStr(today.check_out), label: "Check Out", icon: Flag, color: "text-red-500", bg: "bg-red-50" };
    const se = { time: fmtShift(shift?.end_time), label: "Shift End", icon: Flag, color: "text-slate-400", bg: "bg-slate-100" };
    const coHM = new Date(today.check_out).toLocaleTimeString("en-GB", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: false });
    const coMin = parseInt(coHM.slice(0, 2)) * 60 + parseInt(coHM.slice(3, 5));
    let seMin = 24 * 60;
    let coMinAdj = coMin;
    if (shift?.end_time) {
      const [h, m] = shift.end_time.split(":").map(Number); seMin = h * 60 + m;
      if (shift.start_time) {
        const [sh0, sm0] = shift.start_time.split(":").map(Number); const stMin = sh0 * 60 + sm0;
        if (seMin <= stMin) seMin += 24 * 60; // overnight shift
        if (coMin < stMin) coMinAdj = coMin + 24 * 60; // checkout after midnight
      }
    }
    if (coMinAdj <= seMin) { events.push(co); events.push(se); } else { events.push(se); events.push(co); }
  } else {
    events.push({ time: "Now", label: "Working", icon: CircleDot, color: "text-emerald-500", bg: "bg-emerald-50", live: true });
    events.push({ time: fmtShift(shift?.end_time), label: "Shift End", icon: Flag, color: "text-slate-400", bg: "bg-slate-100" });
  }
  return (
    <div className="relative pl-2">
      {events.map((e, i) => (
        <div key={i} className="flex gap-4 pb-6 last:pb-0 relative">
          {i < events.length - 1 && <span className="absolute left-[19px] top-9 bottom-0 w-px bg-slate-200" />}
          <div className={`w-10 h-10 rounded-full ${e.bg} flex items-center justify-center shrink-0 relative`}>
            <e.icon className={`w-4 h-4 ${e.color} ${e.live ? "pulse-dot" : ""}`} />
          </div>
          <div className="pt-1">
            <p className="font-medium text-slate-800">{e.label}</p>
            <p className="text-sm text-slate-400">{e.time}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

const CAL_COLORS = {
  Present: "bg-emerald-100 text-emerald-700", Late: "bg-amber-100 text-amber-700",
  "Half Day": "bg-orange-100 text-orange-700", Permission: "bg-sky-100 text-sky-700",
  Absent: "bg-red-100 text-red-600", Leave: "bg-blue-100 text-blue-700",
  Holiday: "bg-purple-100 text-purple-700", "Work from Home": "bg-teal-100 text-teal-700",
};

function MonthCalendar({ month, history }) {
  const [y, m] = month.split("-").map(Number);
  const first = new Date(Date.UTC(y, m - 1, 1)).getUTCDay();
  const dim = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const byDate = Object.fromEntries(history.map((a) => [a.date, a]));
  const cells = [...Array(first).fill(null), ...Array.from({ length: dim }, (_, i) => i + 1)];
  const used = [...new Set(history.map((a) => a.status))];
  return (
    <div className="p-3 sm:p-5">
      <div className="grid grid-cols-7 gap-1 sm:gap-2 text-center text-[10px] sm:text-xs text-slate-400 mb-1">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1 sm:gap-2">
        {cells.map((d, i) => {
          if (!d) return <div key={"e" + i} />;
          const rec = byDate[`${month}-${String(d).padStart(2, "0")}`];
          return (
            <div key={d} title={rec ? `${rec.status}${rec.hours ? " · " + rec.hours + "h" : ""}${rec.is_manual ? ` · Manually changed: ${rec.manual_from || "—"} → ${rec.manual_to || rec.status} by ${rec.changed_by_name || "Admin"}${rec.changed_by_role ? " (" + rec.changed_by_role + ")" : ""}` : ""}` : ""}
              className={`rounded-lg sm:rounded-xl min-h-[44px] sm:min-h-[64px] p-1 sm:p-2 flex flex-col justify-between border border-slate-100 ${rec ? CAL_COLORS[rec.status] || "bg-slate-100 text-slate-600" : "bg-white text-slate-400"}`}>
              <span className="text-xs sm:text-sm font-semibold">{d}</span>
              {rec && <span className="text-[8px] sm:text-[10px] leading-tight font-medium truncate">{rec.status}</span>}
            </div>
          );
        })}
      </div>
      {used.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-4">
          {used.map((s) => <span key={s} className={`text-[11px] px-2 py-0.5 rounded-full ${CAL_COLORS[s] || "bg-slate-100 text-slate-600"}`}>{s}</span>)}
        </div>
      )}
    </div>
  );
}

function StaffAttendance() {
  const { employee } = useAuth();
  const [data, setData] = useState({ today: null, history: [] });
  const [shift, setShift] = useState(null);
  const [range, setRange] = useState("month");
  const [month, setMonth] = useState("");
  const [view, setView] = useState("list");
  const calMonth = month || new Date().toISOString().slice(0, 7);

  const load = async (r = range, m = month) => { const { data } = await api.get("/attendance/me", { params: m ? { month: m } : { range: r } }); setData(data); };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (employee?.shift_id) api.get("/shifts").then((res) => setShift(res.data.find((s) => s.id === employee.shift_id) || null)); }, [employee]);
  const onRange = (r) => { setRange(r); setMonth(""); load(r, ""); };
  const onMonth = (m) => { setMonth(m); load(range, m); };

  return (
    <div className="space-y-6">
      <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight md:hidden">Attendance</h1>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2"><CheckInCard today={data.today} shift={shift} onChange={() => load()} /></div>
        <div className="rounded-2xl bg-white border border-slate-200 p-5">
          <h2 className="font-heading font-semibold text-slate-800 mb-4">Workday Timeline</h2>
          <Timeline today={data.today} shift={shift} />
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 flex-wrap gap-2">
          <h2 className="font-heading font-semibold text-slate-800">History</h2>
          <div className="flex flex-wrap items-center gap-2">
          <input type="month" value={month} max={new Date().toISOString().slice(0, 7)} onChange={(e) => onMonth(e.target.value)} data-testid="att-month"
            className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs text-slate-700" />
          <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
            {[["list", List], ["calendar", CalendarDays]].map(([v, Icon]) => (
              <button key={v} onClick={() => setView(v)} data-testid={`view-${v}`} title={v}
                className={`px-2.5 py-1 rounded-lg transition-colors ${view === v ? "bg-white text-slate-800 shadow-sm" : "text-slate-500"}`}><Icon className="w-3.5 h-3.5" /></button>
            ))}
          </div>
          <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
            {["today", "week", "month"].map((r) => (
              <button key={r} onClick={() => onRange(r)} data-testid={`range-${r}`}
                className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${!month && range === r ? "bg-white text-slate-800 shadow-sm" : "text-slate-500"}`}>
                {r === "today" ? "Today" : r === "week" ? "This Week" : "This Month"}
              </button>
            ))}
          </div>
          </div>
        </div>
        {view === "calendar" ? <MonthCalendar month={calMonth} history={data.history} /> : data.history.length === 0 ? <EmptyState icon={Coffee} title="No records for this period" /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-500 text-xs">
                <th className="font-medium px-5 py-2">Date</th><th className="font-medium px-3 py-2">Check In</th>
                <th className="font-medium px-3 py-2">Check Out</th><th className="font-medium px-3 py-2">Hours</th><th className="font-medium px-5 py-2">Status</th>
              </tr></thead>
              <tbody>
                {data.history.map((a) => (
                  <tr key={a.id} className="border-t border-slate-50 hover:bg-slate-50/80 transition-colors">
                    <td className="px-5 py-3 font-medium text-slate-700">{shortDate(a.date)}</td>
                    <td className="px-3 py-3 text-slate-600">
                      <div>{timeStr(a.check_in)}</div><LocLink addr={a.check_in_address} lat={a.check_in_lat} lng={a.check_in_lng} />
                      {a.check_in_office && (
                        <div className="flex items-center gap-1 text-[11px] text-emerald-600 font-medium mt-0.5">
                          <MapPin className="w-3 h-3" />
                          <span>{a.check_in_office}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3 text-slate-600">{timeStr(a.check_out)}<LocLink addr={a.check_out_address} lat={a.check_out_lat} lng={a.check_out_lng} /></td>
                    <td className="px-3 py-3 text-slate-600">{a.hours ? `${a.hours}h` : "—"}</td>
                    <td className="px-5 py-3"><StatusBadge status={a.status} /><ManualChangeNote rec={a} />{a.status_reason_by && <p className="text-[11px] text-slate-500 mt-1 max-w-[220px]">Marked by {a.status_reason_by}</p>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function AdminAttendance({ user }) {
  const [rows, setRows] = useState([]);
  const [depts, setDepts] = useState([]);
  const [locs, setLocs] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [dept, setDept] = useState("all");
  const [status, setStatus] = useState("all");
  const [locFilter, setLocFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selEmp, setSelEmp] = useState(null);

  const load = async () => {
    const params = { date };
    if (dept !== "all") params.department = dept;
    if (status !== "all") params.status = status;
    if (locFilter !== "all") params.location = locFilter;
    const { data } = await api.get("/attendance", { params });
    setRows(data);
  };
  useEffect(() => {
    api.get("/departments").then((r) => setDepts(r.data));
    api.get("/locations").then((r) => setLocs(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [date, dept, status, locFilter]);

  const [hd, setHd] = useState(null);
  const [hdReason, setHdReason] = useState("");
  const mark = async (empId, st, reason) => {
    if (st === "Half Day" && !reason) { const r = rows.find((x) => x.employee_id === empId); setHdReason(""); setHd({ empId, name: r?.employee_name || r?.name || "" }); return; }
    await api.post("/attendance/mark", null, { params: { employee_id: empId, date, status: st, ...(reason ? { reason } : {}) } }); toast.success("Attendance updated"); load();
  };
  const submitHd = async () => { if (!hdReason.trim()) return; await mark(hd.empId, hd.status || "Half Day", hdReason.trim()); setHd(null); };

  const counts = { Present: 0, Late: 0, "Half Day": 0, Permission: 0, Absent: 0, Leave: 0, Holiday: 0, "Week Off": 0, "Work from Home": 0 };
  rows.forEach((r) => { if (counts[r.status] !== undefined) counts[r.status]++; });

  const filteredRows = rows.filter(r => 
    r.name.toLowerCase().includes(search.toLowerCase()) || 
    (r.location || "").toLowerCase().includes(search.toLowerCase()) ||
    (r.check_in_office || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-9 gap-3">
        {[["Present", "text-emerald-600"], ["Late", "text-red-600"], ["Half Day", "text-red-600"], ["Permission", "text-amber-600"], ["Absent", "text-red-600"], ["Leave", "text-blue-600"], ["Holiday", "text-sky-600"], ["Week Off", "text-slate-500"], ["Work from Home", "text-purple-600"]].map(([k, c]) => (
          <div key={k} className="rounded-2xl bg-white border border-slate-200 p-4">
            <p className={`font-heading text-2xl font-bold ${c}`}>{counts[k]}</p>
            <p className="text-xs text-slate-500">{k}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-3">
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="att-date" className="rounded-xl w-44 bg-white" />
        <Select value={locFilter} onValueChange={setLocFilter}>
          <SelectTrigger className="rounded-xl w-40 bg-white" data-testid="att-loc"><SelectValue placeholder="All Locations" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Locations</SelectItem>
            {locs.map((l) => <SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={dept} onValueChange={setDept}>
          <SelectTrigger className="rounded-xl w-40 bg-white" data-testid="att-dept"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Departments</SelectItem>{depts.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="rounded-xl w-36 bg-white" data-testid="att-status"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Status</SelectItem>{["Present", "Late", "Absent", "Leave", "Holiday", "Week Off", "Work from Home", "Half Day"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Input 
          placeholder="Search staff or location..." 
          value={search} 
          onChange={(e) => setSearch(e.target.value)} 
          className="rounded-xl w-64 bg-white"
        />
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-slate-500 text-xs">
              <th className="font-medium px-5 py-3">Employee</th>
              <th className="font-medium px-3 py-3">Location</th>
              <th className="font-medium px-3 py-3">Check In</th>
              <th className="font-medium px-3 py-3">Check Out</th>
              <th className="font-medium px-3 py-3">Hours</th>
              <th className="font-medium px-3 py-3">Status</th>
              {(user.role === "admin" || user.role === "admin_staff") && <th className="font-medium px-5 py-3">Mark</th>}
            </tr></thead>
            <tbody>
              {filteredRows.map((r) => (
                <tr key={r.employee_id} onClick={(ev) => { if (!ev.target.closest("button,input,select,[role=combobox],[role=option],[role=listbox]")) setSelEmp(r); }} className="cursor-pointer border-t border-slate-50 hover:bg-slate-50/80 transition-colors">
                  <td className="px-5 py-3"><div className="flex items-center gap-2.5"><Avatar src={r.photo} name={r.name} size={30} /><span className="font-medium text-slate-700">{r.name}</span></div></td>
                  <td className="px-3 py-3 text-slate-600">
                    <div className="flex flex-col">
                      <span className="font-medium text-slate-700 text-xs flex items-center gap-1">
                        {r.check_in_office ? (
                          <>
                            <MapPin className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                            {r.check_in_office}
                          </>
                        ) : (
                          r.location || "—"
                        )}
                      </span>
                      {r.check_in_place && (
                        <span className="text-[10px] text-emerald-600 font-medium">
                          {r.check_in_place === "hub" ? "Hub Check-in" : "Office Check-in"}
                          {r.check_in_distance_m !== null && r.check_in_distance_m !== undefined ? ` · ${r.check_in_distance_m}m away` : ""}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-slate-600">{timeStr(r.check_in)}<LocLink addr={r.check_in_address} lat={r.check_in_lat} lng={r.check_in_lng} /></td>
                  <td className="px-3 py-3 text-slate-600">{timeStr(r.check_out)}<LocLink addr={r.check_out_address} lat={r.check_out_lat} lng={r.check_out_lng} /></td>
                  <td className="px-3 py-3 text-slate-600">{r.hours ? `${r.hours}h` : "—"}</td>
                  <td className="px-3 py-3"><StatusBadge status={r.status} /><ManualChangeNote rec={r} />{(r.status_reason || r.status === "Half Day") && <p className="text-[11px] text-slate-500 mt-1 max-w-[200px] flex items-center gap-1"><span className="truncate" title={r.status_reason || ""}>{r.status_reason || "Add reason"}{r.status_reason_by ? " — " + r.status_reason_by : ""}</span><button type="button" title="Edit reason" onClick={(ev) => { ev.stopPropagation(); setHdReason(r.status_reason || ""); setHd({ empId: r.employee_id, name: r.name, status: r.status }); }} className="text-emerald-600 hover:text-emerald-800 shrink-0"><Pencil className="w-3 h-3" /></button></p>}</td>
                  {(user.role === "admin" || user.role === "admin_staff") && (
                    <td className="px-5 py-3">
                      <Select value={r.status} onValueChange={(v) => mark(r.employee_id, v)}>
                        <SelectTrigger className="rounded-lg h-8 w-28 text-xs" data-testid={`mark-${r.employee_id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{["Present", "Late", "Absent", "Leave", "Holiday", "Week Off", "Work from Home", "Half Day"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <Dialog open={!!hd} onOpenChange={(o) => { if (!o) setHd(null); }}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>Half Day Reason{hd?.name ? " – " + hd.name : ""}</DialogTitle></DialogHeader>
          <textarea autoFocus rows={3} value={hdReason} onChange={(ev) => setHdReason(ev.target.value)} placeholder="Enter reason..." className="w-full rounded-xl border border-slate-200 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
          <DialogFooter><Button variant="outline" className="rounded-xl" onClick={() => setHd(null)}>Cancel</Button><Button disabled={!hdReason.trim()} onClick={submitHd} className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white">Submit</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <AttendanceMonthDialog employee={selEmp} onClose={() => setSelEmp(null)} />
    </div>
  );
}

function LocLink({ addr, lat, lng }) {
  if (!addr && (lat === null || lat === undefined)) return null;
  const href = lat !== null && lat !== undefined ? `https://www.google.com/maps?q=${lat},${lng}` : `https://www.google.com/maps/search/${encodeURIComponent(addr)}`;
  return (
    <a href={href} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} title={addr || ""}
      className="mt-0.5 flex items-center gap-0.5 text-[10px] text-emerald-700 hover:underline max-w-[200px]">
      <MapPin className="w-3 h-3 shrink-0" /><span className="truncate">{addr || `${Number(lat).toFixed(5)}, ${Number(lng).toFixed(5)}`}</span>
    </a>
  );
}
